import React, { useState } from "react";
import { FormSection } from "../../../../../components/FormSection/FormSection";
import { Divider } from "../../../../../components/core/Divider/Divider";
import {
  CC_LABEL,
  PC_HISTORY,
  COMPANY_DATA,
  CONTRACT,
  CONTRACT_TYPE,
  DATA_DELETED_SUCCESS_LABEL,
  DELETE_ASSIGN_PC_LABEL,
  EDIT_PC_LABEL,
  EMAIL_LABEL,
  EMPTY_CM_SHEET_MESSAGE,
  EMPTY_STATE_PERFORMANCE_COACH_MESSAGE,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  FISCAL_CODE_LABEL,
  HEADQUARTERS_LABEL,
  HIRE_DATE_LABEL,
  INTERNAL_GRADE_LABEL,
  NAME_LABEL,
  PERSONAL_DATA,
  PRACTICE_LABEL,
  SURNAME_LABEL,
  TERMINATION_DATE_LABEL,
} from "../../../../../constants/Dictionary";
import { ReactComponent as UserBoxIcon } from "../../../../../icons/userBox.svg";
import { ReactComponent as ContractIcon } from "../../../../../icons/details.svg";
import { ReactComponent as CompanyIcon } from "../../../../../icons/company.svg";
import { ReactComponent as GraduationIcon } from "../../../../../icons/graduation.svg";
import { ReactComponent as EditIcon } from "../../../../../icons/pencil.svg";
import dayjs from "dayjs";
import { collaboratorCmColumns } from "../../../../../constants/TableColumns";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { AssignCMModal } from "./AssignCMModal/AssignCMModal";
import { roleHasPermission } from "../../../../../utils/roleAuth.utils";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { TextBoxGrid } from "../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { Tooltip } from "../../../../../components/core/Tooltip/Tooltip";
import {
  findCCById,
  findPracticeById,
} from "../../../../../utils/orgFunctions.utils";
import {
  Table,
  TableThemeEnum,
} from "../../../../../components/core/Table/Table";
import {
  CollaboratorContractTypeEnum,
  PermissionsEnum,
} from "../../../../../constants/AppConstants";
import {
  CollaboratorCM,
  CollaboratorCmSheet,
  CollaboratorDetails,
} from "../../../../../types/api/collaborators.type";
import { IT_DATE_FORMAT } from "../../../../../utils/date/date.utils";
import { PopupConfirm } from "../../../../../components/PopupConfirm/PopupConfirm";
import { useNotification } from "../../../../../hooks/notification.hook";
import {
  useCareerManagersProfile,
  useDeleteCollaboratorCM,
} from "../../../../../hooks/collaboratorProfile.hook";

type CollaboratorProfileProps = {
  collaborator: CollaboratorDetails;
  currentCmSheet: CollaboratorCmSheet | null;
  isMyProfile?: boolean;
};
export const CollaboratorProfile = ({
  collaborator,
  isMyProfile,
  currentCmSheet,
}: CollaboratorProfileProps) => {
  const { notification } = useNotification();
  const [showModal, setShowModal] = useState(false);
  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const profileId = collaborator.id;
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedCM, setSelectedCM] = useState<CollaboratorCM | null>(null);

  const { data: collaboratorCmList, isFetching: cmListLoading } =
    useCareerManagersProfile(profileId);
  const { mutateAsync: deleteCollaboratorCM, isPending: isDeleting } =
    useDeleteCollaboratorCM(profileId);

  const hasCmSheet = !!currentCmSheet;

  const collaboratorData = {
    ...collaborator,
    cmList: collaboratorCmList || [],
  };

  const isInterim =
    collaboratorData.contractType === CollaboratorContractTypeEnum.INT;

  const onDelete = (record: CollaboratorCM) => {
    setSelectedCM(record);
    setOpenDeleteModal(true);
  };

  const handleDeleteAssignCM = async () => {
    if (!selectedCM) {
      return;
    }

    try {
      await deleteCollaboratorCM(selectedCM.id);
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
    } catch (error) {
      console.error(`Error deleting the assigned PC: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    } finally {
      setOpenDeleteModal(false);
      setSelectedCM(null);
    }
  };

  const canDeleteAssignCM =
    !isMyProfile &&
    roleHasPermission(profile, PermissionsEnum.CAN_DELETE_ASSIGN_CM);

  const rowHasDelete = (row: CollaboratorCM) => row.endDate === null;

  const getPopupText = () => {
    return selectedCM
      ? `Sei sicuro di voler rimuovere il PC ${selectedCM?.name} ${selectedCM?.surname} assegnato all'utente ${collaborator?.name} ${collaborator?.surname}?`
      : "";
  };

  const hideDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedCM(null);
  };

  return (
    <>
      <>
        <FormSection label={PERSONAL_DATA} icon={<UserBoxIcon />}>
          <TextBoxGrid
            numCol={4}
            items={[
              {
                label: NAME_LABEL,
                text: collaboratorData.name,
              },
              {
                label: SURNAME_LABEL,
                text: collaboratorData?.surname,
              },
              {
                label: FISCAL_CODE_LABEL,
                text: collaboratorData?.taxIdCode,
              },
              ...(isInterim
                ? []
                : [
                    {
                      label: EMAIL_LABEL,
                      text: collaboratorData?.email,
                    },
                  ]),
            ]}
          />
        </FormSection>
        <Divider />
        <FormSection label={CONTRACT} icon={<ContractIcon />}>
          <TextBoxGrid
            numCol={4}
            items={[
              {
                label: CONTRACT_TYPE,
                text: collaboratorData?.contractType,
              },
            ]}
          ></TextBoxGrid>
        </FormSection>
        <Divider />
        <FormSection label={COMPANY_DATA} icon={<CompanyIcon />}>
          <TextBoxGrid
            numCol={4}
            items={[
              {
                label: HEADQUARTERS_LABEL,
                text: collaboratorData?.companyHeadquarter,
              },
              {
                label: INTERNAL_GRADE_LABEL,
                text: collaboratorData?.internalGrade?.toString(),
              },
              {
                label: HIRE_DATE_LABEL,
                text:
                  collaboratorData?.hireDate &&
                  dayjs(collaboratorData?.hireDate).format(IT_DATE_FORMAT),
              },
              {
                label: TERMINATION_DATE_LABEL,
                text:
                  collaboratorData?.terminationDate &&
                  dayjs(collaboratorData?.terminationDate).format(
                    IT_DATE_FORMAT,
                  ),
              },
              {
                label: PRACTICE_LABEL,
                text: collaboratorData?.practice
                  ? findPracticeById(
                      orgSettings.areaList.all,
                      collaboratorData.practice,
                    )?.name
                  : null,
              },
              {
                label: CC_LABEL,
                text: collaboratorData?.cc
                  ? findCCById(orgSettings.areaList.all, collaboratorData.cc)
                      ?.name
                  : null,
              },
            ]}
          />
        </FormSection>
        <Divider />
        <FormSection
          label={PC_HISTORY}
          icon={<GraduationIcon />}
          rightContent={
            roleHasPermission(profile, PermissionsEnum.EDIT_CM) &&
            !isMyProfile && (
              <Tooltip message={EMPTY_CM_SHEET_MESSAGE} hide={hasCmSheet}>
                <Button
                  label={EDIT_PC_LABEL}
                  variant={ButtonVariants.SECONDARY}
                  size={ButtonSizes.SMALL}
                  icon={<EditIcon />}
                  onClick={() => {
                    setShowModal(true);
                  }}
                  disabled={!hasCmSheet}
                />
              </Tooltip>
            )
          }
        >
          <Table
            theme={TableThemeEnum.DARK}
            loading={cmListLoading}
            headers={collaboratorCmColumns}
            data={collaboratorData.cmList || []}
            emptyText={EMPTY_STATE_PERFORMANCE_COACH_MESSAGE}
            onDelete={canDeleteAssignCM ? onDelete : undefined}
            rowHasDelete={canDeleteAssignCM ? rowHasDelete : undefined}
          />
        </FormSection>
      </>
      {collaborator && showModal && (
        <AssignCMModal
          onSave={() => setShowModal(false)}
          onClose={() => setShowModal(false)}
          collaborator={collaborator}
          editMode
        />
      )}
      <PopupConfirm
        isOpen={openDeleteModal}
        onCancel={hideDeleteModal}
        onConfirm={handleDeleteAssignCM}
        label={DELETE_ASSIGN_PC_LABEL}
        popupText={getPopupText()}
        isLoading={isDeleting}
      />
    </>
  );
};
